import styled from "styled-components";
import { Link } from "react-router-dom";
import {
  gradientTextStyles,
  Page,
  theme,
  typography,
} from "../Homepage/styles";

export const AnalystsPage = styled(Page)`
  background: hsl(var(--hp-background));
  background-image: none;
`;

export const AnalystsHeaderSection = styled.section`
  padding: 96px 16px 48px;
  text-align: center;

  @media (min-width: 768px) {
    padding: 112px 16px 64px;
  }
`;

export const AnalystsHeaderInner = styled.div`
  max-width: 42rem;
  margin: 0 auto;
`;

export const AnalystsTitle = styled.h1`
  margin: 0 0 16px;
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 1.875rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.2;
  letter-spacing: ${typography.tracking.tight};

  @media (min-width: 768px) {
    font-size: 3rem;
  }

  span {
    ${gradientTextStyles}
  }
`;

export const AnalystsLead = styled.p`
  margin: 0;
  color: ${theme.textMuted};
  font-size: 1rem;
  line-height: 1.6;

  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
`;

export const AnalystsGridSection = styled.section`
  padding: 0 16px 96px;
`;

export const AnalystsGridInner = styled.div`
  max-width: 80rem;
  margin: 0 auto;
`;

export const AnalystsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

export const AnalystCardLink = styled(Link)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow: hidden;
  padding: 24px;
  border-radius: 1rem;
  border: 1px solid hsl(var(--hp-primary) / 0.3);
  background: hsl(var(--hp-card) / 0.9);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow: var(--hp-shadow-card);
  color: inherit;
  text-align: center;
  text-decoration: none;
  transition:
    transform 0.3s ease,
    border-color 0.3s ease,
    background-color 0.3s ease,
    box-shadow 0.3s ease;

  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
    color: inherit;
  }

  &:hover *,
  &:focus *,
  &:active *,
  &:visited * {
    text-decoration: none;
  }

  &:hover {
    transform: translateY(-4px);
    border-color: hsl(var(--hp-primary) / 0.5);
    background: hsl(var(--hp-card));
    box-shadow: 0 0 40px hsl(var(--hp-primary) / 0.15);
  }

  &:focus-visible {
    outline: 2px solid hsl(var(--hp-primary));
    outline-offset: 2px;
  }
`;

export const AnalystCardTopAccent = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 3px;
  border-radius: 1rem 1rem 0 0;
  background: linear-gradient(
    to right,
    hsl(var(--hp-primary)) 0%,
    hsl(var(--hp-gold)) 50%,
    hsl(var(--hp-primary)) 100%
  );
  box-shadow: 0 2px 8px hsl(var(--hp-primary) / 0.45);
`;

export const AnalystFeaturedBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: calc(100% - 12px);
  padding: 6px 10px;
  border-bottom-left-radius: 8px;
  border-top-right-radius: 15px;
  background: linear-gradient(
    to right,
    hsl(var(--hp-primary)) 0%,
    hsl(var(--hp-gold)) 100%
  );
  box-shadow: 0 4px 12px hsl(0 0% 0% / 0.2);
  color: hsl(var(--hp-primary-foreground));
  font-size: 0.625rem;
  font-weight: ${typography.weights.bold};
  letter-spacing: 0.08em;
  line-height: 1.15;
  text-transform: uppercase;
  pointer-events: none;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
  }
`;

export const AnalystAvatarWrap = styled.div`
  width: 96px;
  height: 96px;
  margin-bottom: 20px;
  overflow: hidden;
  border-radius: 9999px;
  background: hsl(var(--hp-secondary));
  box-shadow: 0 0 0 3px hsl(var(--hp-primary) / 0.4);
  transition:
    box-shadow 0.3s ease,
    filter 0.3s ease;

  @media (min-width: 768px) {
    width: 112px;
    height: 112px;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${AnalystCardLink}:hover & {
    box-shadow:
      0 0 0 3px hsl(var(--hp-primary)),
      0 0 22px hsl(var(--hp-primary) / 0.55);
  }
`;

export const AnalystNameRow = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
`;

export const AnalystName = styled.h2`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.25rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.3;
`;

export const AnalystVerifiedIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${theme.primary};

  svg {
    display: block;
    width: 20px;
    height: 20px;
  }
`;

export const AnalystRole = styled.p`
  margin: 0 0 12px;
  color: ${theme.textMuted};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  line-height: 1.4;
`;

export const AnalystBio = styled.p`
  display: -webkit-box;
  width: 100%;
  margin: 0 0 20px;
  overflow: hidden;
  color: hsl(var(--hp-muted-foreground) / 0.9);
  font-size: 0.875rem;
  line-height: 1.65;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  flex: 1 1 auto;
`;

export const AnalystCtaDivider = styled.div`
  width: 100%;
  height: 1px;
  margin-bottom: 20px;
  background: linear-gradient(
    to right,
    transparent 0%,
    hsl(var(--hp-primary) / 0.3) 50%,
    transparent 100%
  );
`;

export const AnalystCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: auto;
  color: ${theme.primary};
  font-size: 0.875rem;
  font-weight: ${typography.weights.semibold};
  transition: gap 0.3s ease;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }

  ${AnalystCardLink}:hover & {
    gap: 10px;
  }
`;

export const AnalystSkeletonGrid = styled(AnalystsGrid)`
  pointer-events: none;
`;

export const AnalystSkeletonCard = styled.div`
  height: 360px;
  border-radius: 1rem;
  border: 1px solid hsl(var(--hp-border) / 0.5);
  background: hsl(var(--hp-muted) / 0.35);
  animation: analysts-skeleton-pulse 1.5s ease-in-out infinite;

  @keyframes analysts-skeleton-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.55;
    }
  }
`;
